import { useEffect, useContext, useRef } from 'react'
import { SocketContext } from 'supportAssistant/contexts/SocketContext'
import type { EventType } from 'api/supportAssistant'

// TODO: Replace it with contract event bodies
export type EventBodies = unknown

export default function useSocketEvent<T extends EventBodies>(eventType?: EventType, callback: (event: T, ...rest: Array<any>) => void = () => {}) {
  const { socket } = useContext(SocketContext)

  const emitSocketEvent = <T extends EventBodies>(payload: T) => {
    socket?.emit(eventType as EventType, payload)
  }

  // The listener is registered once per socket/event and reads the latest callback through a ref.
  // Re-subscribing whenever the callback identity changes would tear the listener down on every
  // render for any caller that passes an inline arrow, leaving a window in which a message that
  // arrives mid-render has nobody to hand it to.
  const callbackRef = useRef(callback)
  useEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    if (!socket || !eventType) return

    // Must be a handler unique to this hook instance, and it must be the same reference we pass to
    // `off`: `socket.off(eventType)` with no handler removes *every* listener for the event, so an
    // unmounting consumer would silently unsubscribe the other consumers of the same event.
    // `message:response` has more than one subscriber, which is how a reply could be delivered to
    // the socket and never rendered.
    const handler = (...args: Array<any>) => {
      // Forward every argument: socket.io passes an ack callback as the last one, and the server
      // logs a delivery failure when a message goes unacknowledged.
      (callbackRef.current as (...args: Array<any>) => void)(...args)
    }

    socket.on(eventType, handler)

    return () => {
      socket.off(eventType, handler)
    }
  }, [socket, eventType])

  return emitSocketEvent
}
